import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import { EditorialIntro, SplitSection } from '../innovher/SplitSection';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';
import { stories } from '../../mock';

const FORMATS = [
  {
    code: '01',
    name: "Founders' Night",
    tagline: 'When the world sleeps, founders build.',
    body: 'An after-hours gathering at Studio I designed for founders to step away from the everyday grind, recharge through candid conversations, exchange experiences and rediscover the energy to keep building. Less conference. More community.',
  },
  {
    code: '02',
    name: 'Enablers Engage',
    tagline: 'Deep conversations. Strategic connections.',
    body: 'A focused sector roundtable bringing together domain experts, businesses, investors and ecosystem enablers for a deeper look at an emerging opportunity. Each edition also introduces selected ventures or products, opening conversations around partnerships, pilots and market access.',
  },
  {
    code: '03',
    name: 'Fish Tank',
    tagline: 'Pitch. Question. Validate.',
    body: 'A playful inversion of the conventional pitch room. Three selected founders put their ideas before an engaged audience, not simply to raise capital, but to test assumptions, hear questions, gather perspectives and understand how the market responds.',
  },
  {
    code: '04',
    name: 'Rajasthan Startup Stories',
    tagline: 'Stories worth building from.',
    body: 'A podcast with a purpose: documenting founders, businesses and ecosystem builders shaping entrepreneurship across Rajasthan. Beyond success stories, conversations explore decisions, setbacks, pivots and lessons that can help the next generation build better.',
  },
];

const PUBLICATION_TYPES = [
  'Ecosystem Reports',
  'Original Research',
  'Venture Insights',
  'Trend Studies',
  'Playbooks',
  'Perspectives',
];

const PUBLICATION_THEMES = [
  'Rajasthan Startup Ecosystem',
  'The D2C Revolution',
  'AI as the New Co-founder',
  'Changing capital models',
  'Next-generation businesses',
  'Emerging sectors',
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function FormatsShowcase() {
  const [active, setActive] = useState(0);
  const item = FORMATS[active];

  return (
    <div className="mt-12 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      <div className="lg:col-span-4 lg:sticky lg:top-28">
        <div className="flex lg:flex-col gap-2 overflow-x-auto pb-1 lg:pb-0 -mx-1 px-1">
          {FORMATS.map((f, i) => {
            const on = i === active;
            return (
              <button
                key={f.name}
                type="button"
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className={`shrink-0 lg:w-full text-left px-4 py-3.5 rounded-2xl border transition-all ${on
                  ? 'border-[#d42b92]/55 bg-[#d42b92]/12'
                  : 'border-transparent hover:border-white/15 hover:bg-white/[0.02]'
                  }`}
              >
                <div className="flex items-baseline gap-3">
                  <span className={`text-[11px] font-semibold tracking-[0.22em] ${on ? 'text-[#ff8ec5]' : 'text-white/30'}`}>
                    {f.code}
                  </span>
                  <span className={`text-sm sm:text-base font-medium leading-snug ${on ? 'text-white' : 'text-white/55'}`}>
                    {f.name}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-8">
        <div
          key={item.name}
          className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/16 via-white/[0.025] to-transparent p-7 sm:p-10 lg:p-12 min-h-[320px]"
        >
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#d42b92]/20 blur-3xl pointer-events-none" />
          <div className="relative">
            {/* <div className="text-[#ff8ec5] text-[11px] tracking-[0.28em] uppercase mb-5">
              Signature format {item.code}
            </div> */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white tracking-tight leading-[1.1]">
              {item.name}
            </h3>
            <p className="mt-4 text-lg sm:text-xl text-white/85 font-medium leading-snug max-w-2xl">
              {item.tagline}
            </p>
            <div className="mt-6 h-px w-24 bg-gradient-to-r from-[#d42b92] to-transparent" />
            <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
              {item.body}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IssiPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="iSSi | Because Innovation Needs a Society That Shows Up"
        description="iSSi, InnovHer Startup Support Initiative, nurtures the environment around entrepreneurship through Founders' Night, Enablers Engage, Fish Tank, Rajasthan Startup Stories and publications."
        keywords="iSSi, InnovHer Startup Support Initiative, Founders Night, Enablers Engage, Fish Tank, Rajasthan Startup Stories, Jaipur"
      />

      <PageHeader
        eyebrow="InnovHer Startup Support Initiative"
        title={
          <>
            iSSi
            <span className="block mt-3 sm:mt-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-white/85 tracking-tight leading-[1.15] max-w-4xl">
              Because innovation needs a society that{' '}
              <span className="text-[#ff8ec5]">shows up</span>
            </span>
          </>
        }
        subtitle="Nurturing the environment around entrepreneurship through recurring formats, open conversations and curated experiences."
        image={STOCK.issi}
        breadcrumbs={[
          { label: 'Infrastructure', to: '/#infrastructure' },
          { label: 'iSSi' },
        ]}
      />

      {/* Purpose */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="The purpose"
            title={
              <>
                Show up while startups are still being <span className="text-[#d42b92]">built</span>.
              </>
            }
            lead="Innovation begins with individual ambition, but rarely grows in isolation. It needs curiosity, conversations, validation, encouragement and a community willing to participate in the journey."
            logo="/new_images/logos/issi.webp"
          >
            <p>
              iSSi, InnovHer Startup Support Initiative, is InnovHer’s commitment to nurturing the
              environment around entrepreneurship. Through recurring formats, open conversations and
              curated experiences, iSSi brings founders closer to business leaders, enablers,
              investors, young minds and the wider community.
            </p>
            <p>
              The purpose is simple: make innovation more visible, conversations more meaningful and
              entrepreneurship more participative, creating a regional ecosystem that doesn't merely
              celebrate successful startups, but shows up while they are still being built.
            </p>
          </EditorialIntro>
        </div>
      </section>

      {/* Signature formats */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/3 -left-24 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up max-w-3xl">
            <SectionEyebrow>Signature iSSi Formats</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.05]">
              Recurring formats that keep the ecosystem{' '}
              <span className="text-[#d42b92]">in the room</span>
            </h2>
            <p className="mt-5 text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl">
              Four signature experiences designed to bring founders, enablers, investors and the
              wider community into meaningful contact, again and again.
            </p>
          </div>

          <div className="reveal-up">
            <FormatsShowcase />
          </div>

          {/* Full format list for scanability */}
          <div className="reveal-up mt-16 sm:mt-20 space-y-0 border-t border-white/10">
            {FORMATS.map((f) => (
              <article
                key={f.name}
                className="grid lg:grid-cols-12 gap-4 lg:gap-10 py-8 sm:py-10 border-b border-white/10"
              >
                <div className="lg:col-span-1 text-[#ff8ec5] text-[11px] tracking-[0.22em] font-semibold pt-1">
                  {f.code}
                </div>
                <div className="lg:col-span-4">
                  <h3 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
                    {f.name}
                  </h3>
                  <p className="mt-2 text-white/80 text-sm sm:text-base font-medium leading-snug">
                    {f.tagline}
                  </p>
                </div>
                <p className="lg:col-span-7 text-white/55 text-[15px] sm:text-base leading-relaxed">
                  {f.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="iSSi Publications"
            title="Observe. Research. Share."
            lead="Building an innovation ecosystem also means understanding how it is changing."
          >
            <p>
              iSSi Publications brings together original research, ecosystem observations, data and
              perspectives developed by InnovHer’s internal team and collaborators. Through reports,
              studies, insight papers and practical playbooks, the initiative explores shifts shaping
              entrepreneurship and venture building, particularly from the perspective of emerging
              Indian ecosystems.
            </p>
            <p>
              The intent is not simply to report trends, but to create useful knowledge that helps
              the ecosystem think, question and build better.
            </p>
          </EditorialIntro>

          <div className="reveal-up mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="text-[#ff8ec5] text-[11px] tracking-[0.28em] uppercase mb-5">
                What we publish
              </div>
              <ul className="space-y-0 border-t border-white/10">
                {PUBLICATION_TYPES.map((type) => (
                  <li
                    key={type}
                    className="border-b border-white/10 py-4 text-white/85 text-base sm:text-lg font-medium tracking-tight"
                  >
                    {type}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7">
              <div className="text-[#ff8ec5] text-[11px] tracking-[0.28em] uppercase mb-5">
                Themes we explore
              </div>
              <p className="text-white/55 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                Themes range from the Rajasthan Startup Ecosystem and the D2C Revolution to AI as
                the New Co-founder, changing capital models, next-generation businesses and emerging
                sectors.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {PUBLICATION_THEMES.map((theme, i) => (
                  <div
                    key={theme}
                    className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-5 hover:border-[#d42b92]/40 transition-colors"
                  >
                    {/* <div className="text-[#ff8ec5] text-[11px] tracking-[0.2em] uppercase mb-2">
                      {String(i + 1).padStart(2, '0')}
                    </div> */}
                    <p className="text-white/85 text-sm sm:text-base leading-snug">{theme}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stories from the ecosystem */}
      <section id="stories" className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <SplitSection
            className="reveal-up"
            eyebrow="From the ecosystem"
            title={
              <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.08]">
                Stories worth{' '}
                <span className="text-[#d42b92]">building from</span>
              </h2>
            }
          >
            <p>
              Moments, conversations and milestones from across the iSSi community, documenting the
              people and ideas shaping entrepreneurship around us.
            </p>
          </SplitSection>

          <div className="reveal-up mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {stories.map((s, i) => {
              const Card = (
                <article className="group relative rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] hover:border-[#d42b92] transition-all duration-500 h-full">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs font-medium text-white tracking-wide border border-white/20">
                      {s.tag}
                    </div>
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white text-black grid place-items-center group-hover:bg-[#d42b92] group-hover:text-white transition-colors">
                      <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                    </div>
                  </div>
                  <div className="p-5 sm:p-6">
                    <div className="text-xs text-white/40 tracking-wider">{s.date}</div>
                    <h3 className="mt-2 text-lg sm:text-xl font-semibold tracking-tight text-white group-hover:text-[#ff8ec5] transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-white/55 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </article>
              );
              return (
                <div key={s.slug || s.title} className="reveal-up" style={{ transitionDelay: `${i * 50}ms` }}>
                  {s.to ? <Link to={s.to} className="block h-full">{Card}</Link> : Card}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#08080a] py-16 sm:py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="reveal-up relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/20 via-white/[0.03] to-transparent p-8 sm:p-12 lg:p-16 max-w-4xl">
            <div className="absolute -right-20 -bottom-20 h-64 w-64 rounded-full bg-[#d42b92]/25 blur-3xl pointer-events-none" />
            <div className="relative">
              <SectionEyebrow>Be part of iSSi</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.08]">
                Show up for the builders
              </h2>
              <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl">
                Whether you are a founder, enabler, investor or someone who cares about the regional
                innovation economy, iSSi is a place to participate while ventures are still being
                shaped.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#d42b92] px-6 py-3 text-sm font-medium text-white hover:bg-[#ff8ec5] hover:text-black transition-colors"
              >
                Join the iSSi community <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
